import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const navLinks = [
  { label: "nav.home", href: "#listings" },
  { label: "nav.services", href: "#services" },
  { label: "nav.howItWorks", href: "#how-it-works" },
  { label: "nav.returns", href: "#returns" },
  { label: "nav.honestFAQ", href: "#honest-faq" },
];

const pageLinks = [
  { label: "nav.properties", to: "/properties" },
  { label: "nav.coOwnership", to: "/co-ownership" },
  { label: "nav.regions", to: "/regions" },
  { label: "nav.about", to: "/about" },
  { label: "nav.contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleAnchorClick = (href: string) => {
    setMobileOpen(false);
    const id = href.replace("#", "");
    if (isHome) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", href);
      }
    }
  };

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-paper/95 backdrop-blur-md border-b border-hairline"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-3"
            onClick={() => {
              if (isHome) window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <div className="w-10 h-10 rounded-full border border-hairline flex items-center justify-center bg-paper/50">
              <span className="font-jp text-sm text-ink tracking-wider">暖</span>
            </div>
            <span className="font-serif text-xl md:text-2xl font-semibold tracking-wide text-ink">
              NOREN
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Anchor links on home, router links on sub-pages */}
            {navLinks.map((link) =>
              isHome ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleAnchorClick(link.href);
                  }}
                  className="text-sm font-medium text-ink/70 hover:text-ink transition-colors duration-300 cursor-pointer"
                >
                  {t(link.label)}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={`/${link.href}`}
                  className="text-sm font-medium text-ink/70 hover:text-ink transition-colors duration-300"
                >
                  {t(link.label)}
                </Link>
              )
            )}
            {pageLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors duration-300 ${
                  location.pathname === link.to
                    ? "text-ink"
                    : "text-ink/70 hover:text-ink"
                }`}
              >
                {t(link.label)}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            {/* Language toggle */}
            <div className="flex items-center gap-1 border border-hairline rounded-full px-1 py-0.5">
              <button
                onClick={() => changeLang("ja")}
                className={`px-2 py-1 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  i18n.language === "ja" ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"
                }`}
              >
                {t("nav.langJa")}
              </button>
              <button
                onClick={() => changeLang("en")}
                className={`px-2 py-1 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  i18n.language === "en" ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"
                }`}
              >
                {t("nav.langEn")}
              </button>
            </div>

            {isHome ? (
              <a
                href="#inquiry"
                onClick={(e) => {
                  e.preventDefault();
                  handleAnchorClick("#inquiry");
                }}
                className="inline-flex items-center gap-2 bg-ink text-paper text-sm font-medium px-6 py-2.5 rounded-full hover:bg-indigo transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                {t("nav.requestList")}
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            ) : (
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-ink text-paper text-sm font-medium px-6 py-2.5 rounded-full hover:bg-indigo transition-colors duration-300 whitespace-nowrap"
              >
                {t("nav.requestList")}
                <i className="ri-arrow-right-up-line text-sm"></i>
              </Link>
            )}
          </div>

          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-ink"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i
              className={`${
                mobileOpen ? "ri-close-line" : "ri-menu-line"
              } text-xl`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-paper border-t border-hairline max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-8 space-y-5">
            {navLinks.map((link) =>
              isHome ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleAnchorClick(link.href);
                  }}
                  className="block text-base font-medium text-ink/80 hover:text-ink cursor-pointer"
                >
                  {t(link.label)}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={`/${link.href}`}
                  onClick={() => setMobileOpen(false)}
                  className="block text-base font-medium text-ink/80 hover:text-ink"
                >
                  {t(link.label)}
                </Link>
              )
            )}
            {pageLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="block text-base font-medium text-ink/80 hover:text-ink"
              >
                {t(link.label)}
              </Link>
            ))}

            {/* Mobile language toggle */}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => { changeLang("ja"); }}
                className={`px-3 py-1.5 text-sm font-medium border rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  i18n.language === "ja" ? "bg-ink text-paper border-ink" : "text-ink/60 border-hairline"
                }`}
              >
                {t("nav.langJa")}
              </button>
              <button
                onClick={() => { changeLang("en"); }}
                className={`px-3 py-1.5 text-sm font-medium border rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  i18n.language === "en" ? "bg-ink text-paper border-ink" : "text-ink/60 border-hairline"
                }`}
              >
                {t("nav.langEn")}
              </button>
            </div>

            {isHome ? (
              <a
                href="#inquiry"
                onClick={(e) => {
                  e.preventDefault();
                  handleAnchorClick("#inquiry");
                }}
                className="inline-flex items-center gap-2 bg-ink text-paper text-sm font-medium px-6 py-2.5 rounded-full whitespace-nowrap mt-2"
              >
                {t("nav.requestList")}
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            ) : (
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="inline-flex items-center gap-2 bg-ink text-paper text-sm font-medium px-6 py-2.5 rounded-full whitespace-nowrap mt-2"
              >
                {t("nav.requestList")}
                <i className="ri-arrow-right-up-line text-sm"></i>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}